import { gsap } from 'gsap';

declare global {
  interface Window {
    __splashFailSafe?: number;
  }
}

/**
 * Startup splash overlay:
 * - Shows immediately via `html.splash-active` (set inline in index.html)
 * - Crossfades splash_1 -> splash_2, then slides up to reveal the app.
 */
export class SplashScreen {
  private overlay: HTMLElement | null;
  private img1: HTMLImageElement | null;
  private img2: HTMLImageElement | null;
  private appRoot: HTMLElement | null;
  private tl: gsap.core.Timeline | null = null;

  constructor() {
    this.overlay = document.getElementById('splash-screen');
    this.img1 = document.getElementById('splash-img-1') as HTMLImageElement | null;
    this.img2 = document.getElementById('splash-img-2') as HTMLImageElement | null;
    this.appRoot = document.getElementById('app');
  }

  start(): void {
    // If missing markup, don't block the site.
    if (!this.overlay || !this.img1 || !this.img2) {
      this.finishImmediately();
      return;
    }

    // Prevent assistive tech from reading the app while splash is active.
    if (this.appRoot) this.appRoot.setAttribute('aria-hidden', 'true');
    this.overlay.setAttribute('aria-hidden', 'false');

    void this.run();
  }

  private async run(): Promise<void> {
    await this.preload(['/splash_1.jpeg', '/splash_2.jpeg']);

    // Initial states
    // Use full-image presentation (no zoom) to avoid cropping.
    gsap.set(this.img1, { opacity: 1, scale: 1, x: 0, y: 0 });
    gsap.set(this.img2, { opacity: 0, scale: 1, x: 0, y: 0 });
    gsap.set(this.overlay, { opacity: 1, yPercent: 0, visibility: 'visible' });

    // Timing: hold1 → crossfade → hold2 → slide up
    const hold1 = 1.5;
    const crossfade = 2.0;
    const hold2 = 1.5;
    const fadeOut = 1.0;
    const exitAt = hold1 + crossfade + hold2;

    const tl = gsap.timeline({
      defaults: { ease: 'power2.inOut' },
      onComplete: () => this.finish(),
    });

    // Notify when splash starts moving out so hero content motion can start
    tl.add(() => {
      window.dispatchEvent(new CustomEvent('splash-exit-start'));
    }, exitAt);

    // Crossfade (no scaling, keep full image visible)
    tl.to(this.img2, { opacity: 1, duration: crossfade, ease: 'power2.out' }, hold1);
    tl.to(this.img1, { opacity: 0, duration: crossfade, ease: 'power2.inOut' }, hold1);

    // Hold second image
    tl.to(this.img2, { opacity: 1, duration: hold2, ease: 'none' }, hold1 + crossfade);

    // Reveal landing page behind splash during exit movement
    if (this.appRoot) {
      tl.to(
        this.appRoot,
        { opacity: 1, duration: 0.35, ease: 'power2.out' },
        exitAt
      );
    }

    // Exit by moving the splash up out of view
    tl.to(
      this.overlay,
      { yPercent: -110, duration: fadeOut, ease: 'power2.inOut' },
      exitAt
    );

    this.tl = tl;
  }

  private preload(urls: string[]): Promise<void> {
    const tasks = urls.map((src) => {
      return new Promise<void>((resolve) => {
        const img = new Image();
        img.onload = () => resolve();
        img.onerror = () => resolve();
        img.src = src;
        // Best-effort decode to reduce flicker during crossfade.
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        (img as any).decode?.().then(resolve).catch(resolve);
      });
    });
    return Promise.all(tasks).then(() => undefined);
  }

  private finish(): void {
    // Clear fail-safe timer (set inline in index.html)
    if (typeof window.__splashFailSafe === 'number') {
      window.clearTimeout(window.__splashFailSafe);
      delete window.__splashFailSafe;
    }

    document.documentElement.classList.remove('splash-active');

    if (this.overlay) {
      this.overlay.style.display = 'none';
      this.overlay.setAttribute('aria-hidden', 'true');
    }
    if (this.appRoot) this.appRoot.removeAttribute('aria-hidden');

    // Help any layout/ScrollTrigger calculations after visibility change.
    window.dispatchEvent(new Event('resize'));
  }

  private finishImmediately(): void {
    if (typeof window.__splashFailSafe === 'number') {
      window.clearTimeout(window.__splashFailSafe);
      delete window.__splashFailSafe;
    }
    document.documentElement.classList.remove('splash-active');
    if (this.overlay) {
      this.overlay.style.display = 'none';
      this.overlay.setAttribute('aria-hidden', 'true');
    }
    if (this.appRoot) this.appRoot.removeAttribute('aria-hidden');
  }

  dispose(): void {
    this.tl?.kill();
    this.tl = null;
    this.overlay = null;
    this.img1 = null;
    this.img2 = null;
    this.appRoot = null;
  }
}

